/*! Copyright (c) Safe As Milk. All rights reserved. */
class ProductInteractiveOptions extends HTMLElement {
    #boundOnClick;
    #boundOnChange;
    #listeners;
    constructor() {
        super();
        this.#boundOnClick = this.#onClick.bind(this);
        this.#boundOnChange = this.#onChange.bind(this);
    }
    connectedCallback() {
        this.variantsControlElement = this.parentElement;
        this.inputs = this.getOptionsElementsObject();
        this.options = Object.keys(this.inputs).reduce(((options, key) => {
            options[this.inputs[key].position] = key;
            return options;
        }), []);
        this.#listeners = [];
        if (this.dataset.variantId) {
            const id = Number(this.dataset.variantId);
            this.selectVariantById(id);
        }
        setTimeout(() => {
            this.variants = this.variantsControlElement.getVariantData();
            this.currentVariant = this.getCurrentVariant();
            this.setOptionInputsAvailability();
            this.setUpInputListeners();
            this.renderCustomSelects();
        });
        // Close custom selects on outside click or scroll
        document.addEventListener('mousedown', this.handleOutsideClick);
        window.addEventListener('scroll', this.handleScroll, true);
    }
    disconnectedCallback() {
        this.#listeners.forEach((listener => {
            listener.element.removeEventListener(listener.event, listener.handler);
        }));
        this.variantsControlElement.removeEventListener("change", this.variantsControlElement.onVariantChange);
        document.removeEventListener('mousedown', this.handleOutsideClick);
        window.removeEventListener('scroll', this.handleScroll, true);
    }
    getOptionsElementsObject() {
        const options = Array.from(this.variantsControlElement.querySelectorAll("input, select > option"));
        const obj = options.reduce(((finalObject, el) => {
            if (el.nodeName === "INPUT") {
                const optionName = el.name.trim();
                if (!finalObject.hasOwnProperty(optionName)) finalObject[optionName] = {
                    position: Object.keys(finalObject).length,
                    type: "inputs",
                    elements: {}
                };
                finalObject[optionName].elements[el.value] = {
                    element: el
                };
            } else if (el.nodeName === "OPTION") {
                const selectElement = el.parentElement;
                const optionName = selectElement.name.replace("options[", "").replace("]", "").trim();
                if (!finalObject.hasOwnProperty(optionName)) {
                    finalObject[optionName] = {
                        position: Object.keys(finalObject).length,
                        type: "select",
                        elements: {},
                        selectElement: selectElement
                    };
                }
                finalObject[optionName].elements[el.value] = {
                    element: el,
                    text: el.innerHTML.trim()
                };
            }
            return finalObject;
        }), {});
        return obj;
    }
    getCurrentOptions() {
        return Array.from(this.variantsControlElement.querySelectorAll("input:checked, select")).map((input => input.value));
    }
    getCurrentVariant() {
        const options = this.getCurrentOptions();
        const result = this.variants.filter((variant => options.every(((option, index) => variant.options[index] === option))));
        return result[0] || null;
    }
    getVariantById(id) {
        const [result] = this.variants.filter((variant => variant.id === id));
        return result || null;
    }
    optionsAvailableInOtherVariants() {
        const currentVariant = this.getCurrentVariant();
        const availableVariants = this.variants.filter((variant => variant.available && variant.id !== currentVariant.id && variant.options[0] === currentVariant.options[0]));
        return currentVariant.options.reduce(((options, option, i) => {
            if (availableVariants.some((variant => variant.options.map(((o, j) => `${o}_${j}`)).includes(`${option}_${i}`)))) options.push(`${option}_${i}`);
            return options;
        }), []);
    }
    setOptionInputsAvailability() {
        this.currentVariant = this.getCurrentVariant();
        Object.keys(this.inputs).forEach((option => {
            if (this.inputs[option].type === "select") {
                const selectElement = this.inputs[option].selectElement;
                const availableOptionsArr = [];
                const unavailableOptionsArr = [];
                Object.keys(this.inputs[option].elements).forEach((value => {
                    const { element, text } = this.inputs[option].elements[value];
                    // Reset all
                    element.classList.remove('strike-through');
                    element.innerHTML = text;
                }));
                // Determine available/unavailable
                Object.keys(this.inputs[option].elements).forEach((value => {
                    const { element, text } = this.inputs[option].elements[value];
                    let isAvailable = false;
                    // Check if this option is available in any variant
                    for (const variant of this.variants) {
                        const idx = this.options.indexOf(option);
                        if (variant.options[idx] === value && variant.available) {
                            isAvailable = true;
                            break;
                        }
                    }
                    if (isAvailable) {
                        element.innerHTML = text;
                        availableOptionsArr.push(element);
                    } else {
                        element.innerHTML = text;
                        element.classList.add('strike-through');
                        unavailableOptionsArr.push(element);
                    }
                }));
                // Reorder options: available first, then unavailable
                const allOptions = [...availableOptionsArr, ...unavailableOptionsArr];
                allOptions.forEach(opt => selectElement.appendChild(opt));
            } else {
                Object.keys(this.inputs[option].elements).forEach((value => {
                    const { element } = this.inputs[option].elements[value];
                    // Remove previous state
                    if (element.nextElementSibling) {
                        element.nextElementSibling.classList.remove('is-disabled');
                    }
                }));
                
                Object.keys(this.inputs[option].elements).forEach((value => {
                    const { element } = this.inputs[option].elements[value];
                    let isAvailable = false;
                    // Check if this option is available in any variant
                    for (const variant of this.variants) {
                        const idx = this.options.indexOf(option);
                        if (variant.options[idx] === value && variant.available) {
                            isAvailable = true;
                            break;
                        }
                    }

                    if (!isAvailable) {
                        element.nextElementSibling.classList.add('is-disabled');
                    }

                    // Simulate current selection with this value for this option group
                    const simulatedOptions = this.getCurrentOptions();
                    const idx = this.options.indexOf(option);
                    simulatedOptions[idx] = value;

                    // Check if any available variant matches this simulated selection
                    isAvailable = this.variants.some(variant =>
                        variant.available &&
                        variant.options.every((opt, i) => opt === simulatedOptions[i])
                    );

                    if (!isAvailable && element.nextElementSibling) {
                        element.nextElementSibling.classList.add('is-disabled');
                    }
                }));
            }
        }));
    }
    getFirstExistingVariant(option, optionGroup) {
        const optionPosition = this.options.indexOf(optionGroup);
        const existingVariantsWithOption = this.variants.filter((variant => variant.options[optionPosition] === option && (optionPosition === 0 || variant.options.slice(0, optionPosition).every(((o, index) => o === this.currentVariant.options[index])))));
        for (let i = 0, l = existingVariantsWithOption.length; i < l; i += 1) {
            if (existingVariantsWithOption[i].available) return existingVariantsWithOption[i];
        }
        return existingVariantsWithOption.length > 0 ? existingVariantsWithOption[0] : null;
    }
    getExistingVariantFromSelectedOption(option, optionGroup) {
        const options = this.getCurrentOptions();
        const optionPosition = this.options.indexOf(optionGroup);
        options[optionPosition] = option;
        const variant = this.variants.find((v => v.options.every(((val, idx) => val === options[idx]))));
        return variant || null;
    }
    selectVariant(variant, hasFoundMatch = true) {
        const currentFocusedElement = document.activeElement;
        const optionsByGroup = variant.options.reduce(((grouped, option, index) => {
            grouped[this.options[index]] = option;
            return grouped;
        }), {});
        Object.keys(this.inputs).forEach((option => {
            Object.keys(this.inputs[option].elements).forEach((value => {
                const {element: element, text: text} = this.inputs[option].elements[value];
                element.disabled = false;
                if (this.inputs[option].type === "inputs") {
                    element.nextElementSibling.classList.remove("is-disabled");
                    element.checked = false;
                }
                if (this.inputs[option].type === "select") element.innerHTML = text;
                if (optionsByGroup[option] === value) {
                    if (this.inputs[option].type === "inputs") {
                        element.checked = true;

                        let optionTitle = element.parentElement?.closest('.product-form__swatch')?.querySelector('.js-option-title')

                        if (!!optionTitle) {
                            optionTitle.innerText = element.value.trim();
                        }
                    }
                    if (this.inputs[option].type === "select") this.inputs[option].selectElement.value = value;
                }
            }));
        }));
        this.setOptionInputsAvailability();

        if (currentFocusedElement) {
            currentFocusedElement.focus();
        }

        const variantSelect = document.querySelector('.js-product-form .js-variants-select');

        if (!!(variantSelect)) {
            const variantOption = variantSelect.querySelector(`option[value="${variant.id}"]`);
            const inventoryMessage = document.querySelector('.product-form__inventory-message');

            if (!!(variantOption)) {
                const mediaGallery = document.querySelector('.js-product-gallery');
                const productHandle = variantSelect.dataset.productHandle;

                if (!!(mediaGallery)) {
                    const parentSection = mediaGallery.closest('.shopify-section');

                    if (!!(parentSection)) {
                        fetch(`${window.Shopify.routes.root}products/${productHandle}?variant=${variant.id}&section_id=${parentSection.id.replace('shopify-section-', '')}`)
                        .then((response) => response.text())
                        .then(data => {
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(data, 'text/html');
                            const newMediaGallery = doc.querySelector('.js-product-gallery');
                            
                            mediaGallery.innerHTML = newMediaGallery.innerHTML;
                        })
                        .catch(error => {
                            console.error(error);
                        });
                    }
                }

                if (!!(inventoryMessage)) {
                    inventoryMessage.textContent = variantOption.dataset.inventoryMessage;
                }

                const specificationsAccordion = document.querySelector('[data-accordion="specifications"] .product-single__accordion__item-wrap');

                if (!!(specificationsAccordion) && variantOption.dataset.specifications.startsWith('{')) {
                    const specificationsData = JSON.parse(variantOption.dataset.specifications);
                    let specifications = `<div class="js-theme-utility-rte-table-wrap" style="overflow:auto;-webkit-overflow-scrolling:touch"><table><tbody>`;


                    if (specificationsData && specificationsData.hasOwnProperty('specifications')) {
                        for (const [key, value] of Object.entries(specificationsData.specifications)) {
                            specifications += `<tr>
                                                 <td class="f-weight--bold">${key}</td>
                                                 <td>${value}</td>
                                               </tr>`;
                        }
                    }
                    
                    specifications += `</tbody></table></div>`;

                    specificationsAccordion.innerHTML = specifications;
                }
            }
            
            if (!hasFoundMatch) {
                if (!!(inventoryMessage)) {
                    inventoryMessage.textContent = variantSelect.dataset.soldOutText;
                }
            }
        }
    }
    selectVariantById(id) {
        const variant = this.getVariantById(id);
        if (variant) {
            this.selectVariant(variant);
            this.variantsControlElement.onVariantChange();
        }
    }
    selectCurrentOrFirstExistingVariant(option, optionGroup) {
        const selectedVariant = this.getExistingVariantFromSelectedOption(option.trim(), optionGroup.trim());
        if (selectedVariant) {
            this.selectVariant(selectedVariant);
        } else {
            const firstExistingVariant = this.getFirstExistingVariant(option.trim(), optionGroup.trim());
            if (firstExistingVariant) {
                this.selectVariant(firstExistingVariant);
            } else {
                const inventoryMessage = document.querySelector('.product-form__inventory-message');

                if (!!(inventoryMessage)) {
                    inventoryMessage.textContent = this.parentElement.parentElement.querySelector('.js-variants-select')?.dataset.soldOutText;
                }
            }
        }
    }
    setUpInputListeners() {
        this.variantsControlElement.removeEventListener("change", this.variantsControlElement.onVariantChange);
        Object.keys(this.inputs).forEach((optionGroup => {
            if (this.inputs[optionGroup].type === "inputs") {
                Object.keys(this.inputs[optionGroup].elements).forEach((option => {
                    const input = this.inputs[optionGroup].elements[option].element;
                    input.addEventListener("click", this.#boundOnClick);
                    this.#listeners.push({
                        element: input,
                        event: "click",
                        handler: this.#boundOnClick
                    });
                }));
            } else if (this.inputs[optionGroup].type === "select") {
                this.inputs[optionGroup].selectElement.addEventListener("change", this.#boundOnChange);
                this.#listeners.push({
                    element: this.inputs[optionGroup].selectElement,
                    event: "change",
                    handler: this.#boundOnChange
                });
            }
        }));
        this.variantsControlElement.addEventListener("change", this.variantsControlElement.onVariantChange);
    }
    #onClick(e) {
        let optionTitle = e.currentTarget.parentElement?.closest('.product-form__swatch')?.querySelector('.js-option-title')

        if (!!optionTitle) {
            optionTitle.innerText = e.target.value.trim();
        }

        this.selectCurrentOrFirstExistingVariant(e.target.value.trim(), e.target.name.trim());
    }
    #onChange(e) {
        this.selectCurrentOrFirstExistingVariant(e.target.value.trim(), e.target.name.trim().replace("options[", "").replace("]", ""));
    }
    // Helper to render custom selects for all select groups
    renderCustomSelects() {
        Object.keys(this.inputs).forEach(option => {
            if (this.inputs[option].type === 'select') {
                const selectElement = this.inputs[option].selectElement;
                selectElement.style.display = 'none';
                // Remove existing custom select if present
                if (selectElement.nextElementSibling && selectElement.nextElementSibling.classList.contains('custom-select')) {
                    selectElement.nextElementSibling.remove();
                }
                // Create custom dropdown
                const details = document.createElement('details');
                details.className = 'custom-select';
                const summary = document.createElement('summary');
                summary.innerHTML = `<span>${selectElement.options[selectElement.selectedIndex]?.textContent || 'Select'}</span>
                                     <summary-icon>
                                       <span class="icon icon--theme icon--chevron icon--down">
                                         <svg aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M13.8683 4.75L33.1183 24L13.8683 43.25" stroke-linecap="square" vector-effect="non-scaling-stroke"></path></svg>
                                       </span>
                                     </summary-icon>`;
                details.appendChild(summary);
                const optionsList = document.createElement('div');
                optionsList.className = 'custom-select-options';
                optionsList.dataset.qa = 'dropdown-variant-select';
                // Separate available and unavailable options
                const available = [], unavailable = [];
                Object.keys(this.inputs[option].elements).forEach((value, idx) => {
                    const { element } = this.inputs[option].elements[value];
                    const optionDiv = document.createElement('div');
                    optionDiv.className = 'custom-select-option';
                    optionDiv.textContent = element.textContent;
                    optionDiv.dataset.value = value;
                    optionDiv.dataset.qa = `dropdown-variant-option-${idx}`;
                    if (element.classList.contains('strike-through')) {
                        optionDiv.classList.add('strike-through');
                        unavailable.push(optionDiv);
                    } else {
                        available.push(optionDiv);
                    }
                    if (selectElement.value === value) {
                        optionDiv.classList.add('selected');
                    }
                    optionDiv.addEventListener('click', (e) => {
                        e.stopPropagation();
                        selectElement.value = value;
                        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
                        summary.innerHTML = `<span>${optionDiv.textContent}</span>
                                             <summary-icon>
                                               <span class="icon icon--theme icon--chevron icon--down">
                                                 <svg aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M13.8683 4.75L33.1183 24L13.8683 43.25" stroke-linecap="square" vector-effect="non-scaling-stroke"></path></svg>
                                               </span>
                                             </summary-icon>`;
                        details.open = false;
                        // Update selected class
                        optionsList.querySelectorAll('.custom-select-option').forEach(opt => opt.classList.remove('selected'));
                        optionDiv.classList.add('selected');
                    });
                });
                // Append available first, then unavailable
                [...available, ...unavailable].forEach(opt => optionsList.appendChild(opt));
                details.appendChild(optionsList);
                selectElement.parentNode.insertBefore(details, selectElement.nextSibling);
            }
        });
    }
    // Helper to update custom selects when options change
    updateCustomSelects() {
        Object.keys(this.inputs).forEach(option => {
            if (this.inputs[option].type === 'select') {
                const selectElement = this.inputs[option].selectElement;
                const details = selectElement.nextElementSibling;
                if (!details || !details.classList.contains('custom-select')) return;
                const summary = details.querySelector('summary');
                const optionsList = details.querySelector('.custom-select-options');
                // Remove all children
                while (optionsList.firstChild) optionsList.removeChild(optionsList.firstChild);
                // Separate available and unavailable options
                const available = [], unavailable = [];
                Object.keys(this.inputs[option].elements).forEach(value => {
                    const { element } = this.inputs[option].elements[value];
                    const optionDiv = document.createElement('div');
                    optionDiv.className = 'custom-select-option';
                    optionDiv.textContent = element.textContent;
                    optionDiv.dataset.value = value;
                    if (element.classList.contains('strike-through')) {
                        optionDiv.classList.add('strike-through');
                        optionDiv.setAttribute('data-unavailable', 'true');
                        unavailable.push(optionDiv);
                    } else {
                        available.push(optionDiv);
                    }
                    if (selectElement.value === value) {
                        optionDiv.classList.add('selected');
                        summary.innerHTML = `<span>${optionDiv.textContent.replace(' - Sold out', '').replace(' - Out of stock', '')}</span>
                                             <summary-icon>
                                               <span class="icon icon--theme icon--chevron icon--down">
                                                 <svg aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M13.8683 4.75L33.1183 24L13.8683 43.25" stroke-linecap="square" vector-effect="non-scaling-stroke"></path></svg>
                                               </span>
                                             </summary-icon>
                                            </summary-icon>`;
                    }
                    optionDiv.addEventListener('click', (e) => {
                        e.stopPropagation();
                        selectElement.value = value;
                        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
                        summary.innerHTML = `<span>${optionDiv.textContent.replace(' - Sold out', '').replace(' - Out of stock', '')}</span>
                                             <summary-icon>
                                               <span class="icon icon--theme icon--chevron icon--down">
                                                 <svg aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M13.8683 4.75L33.1183 24L13.8683 43.25" stroke-linecap="square" vector-effect="non-scaling-stroke"></path></svg>
                                               </span>
                                             </summary-icon>`;
                        details.open = false;
                        optionsList.querySelectorAll('.custom-select-option').forEach(opt => opt.classList.remove('selected'));
                        optionDiv.classList.add('selected');
                    });
                });
                // Append available first, then unavailable
                [...available, ...unavailable].forEach(opt => optionsList.appendChild(opt));
            }
        });
    }
    // Close all custom selects on outside click
    handleOutsideClick = (e) => {
        document.querySelectorAll('.custom-select[open]').forEach(details => {
            if (!details.contains(e.target)) {
                details.open = false;
            }
        });
    }
    // Close all custom selects on scroll, unless scrolling inside a custom select
    handleScroll = (e) => {
        // If the scroll event target is inside an open custom select, do nothing
        const openCustomSelects = document.querySelectorAll('.custom-select[open]');
        for (const details of openCustomSelects) {
            if (details.contains(e?.target)) {
                return;
            }
        }
        openCustomSelects.forEach(details => {
            details.open = false;
        });
    }
}

customElements.define("product-interactive-options", ProductInteractiveOptions);
//# sourceMappingURL=product-interactive-options.js.map

/* Custom select styles */
const style = document.createElement('style');
style.textContent = `
.custom-select {
  position: relative;
  display: inline-block;
  width: 100%;
  margin-bottom: 1em;
}
.custom-select + .icon {
  display: none;
}
.custom-select summary {
  appearance: none;
  display: flex;
  cursor: pointer;
  font-weight: bold;
  padding: 0.5em 1em;
  border: 1px solid var(--color--border-light);
  border-radius: 4px;
  background: #fff;
}
.custom-select[open] summary {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.custom-select-options {
  position: absolute;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid var(--color--border-light);
  border-top: none;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
.custom-select-option {
  padding: 0.5em 1em;
  cursor: pointer;
}
.custom-select-option:hover,
.custom-select-option.selected {
  background-color: rgba(0, 0, 0, 0.04);
}
.custom-select-option.strike-through {
  color: #999;
  text-decoration: line-through;
}
`;
document.head.appendChild(style);
